import { domainError, type DomainError } from '../../shared/domain-error';
import { err, ok, type Result } from '../../shared/result';
import { GANTTPRO_ERROR, type RowIssue } from './errors';
import {
  cleanText,
  columnOf,
  findHeaderRow,
  readDate,
  readNumber,
  readWbsList,
  WBS_PATTERN,
  type Cell,
  type Matrix,
} from './matrix-cells';

/**
 * Leitura do cronograma exportado do GanttPRO (Fase 25, ADR-161).
 *
 * Função pura sobre a **matriz crua** da planilha — quem abre o arquivo é o leitor de
 * planilha, e este módulo não conhece bytes, rede nem banco. O que o arquivo do cliente
 * tem de peculiar, e que isto resolve:
 *
 * - **banner nas quatro primeiras linhas** e cabeçalho na quinta: o cabeçalho é achado
 *   pelas colunas "Número de EDT" e "Nome da tarefa", onde quer que estejam;
 * - **o nome fica em colunas diferentes**: o GanttPRO recua o nome por nível — a torre na
 *   D, a disciplina na E, o serviço na F, a tarefa na G. Só a G tem cabeçalho ("Nome da
 *   tarefa"); as outras são as colunas **entre** o EDT e ela, de cabeçalho vazio. O nome é
 *   o primeiro texto desse trecho;
 * - **data é serial do Excel com fração** (08:00 e 17:00): vale o dia;
 * - **"Horas estimadas" é horas-homem**, e é a quantidade que o item herda (RN-324).
 *
 * Os grupos ("grupo de tarefas") ficam na lista — o caminho de grupos de uma tarefa
 * (`groupPath`) é o que diz a disciplina e o serviço dela, e o nome da tarefa sozinho não diz.
 */

/** Quantas linhas, do topo, a busca pelo cabeçalho olha. O banner tem quatro. */
const HEADER_SCAN_LIMIT = 60;

const HEADERS = {
  wbs: ['numero de edt'],
  name: ['nome da tarefa'],
  start: ['data de inicio planejada', 'data de inicio'],
  end: ['data final planejada', 'data final'],
  progress: ['progresso (%)', 'progresso'],
  duration: ['duracao (horas)', 'duracao'],
  labor: ['horas estimadas'],
  predecessors: ['antecessor', 'antecessores'],
  type: ['tipo'],
  level: ['nivel'],
} as const;

/** Como a coluna se chama para o usuário, nas mensagens de erro. */
const HEADER_LABEL: Readonly<Record<'start' | 'end', string>> = {
  start: 'Data de início planejada',
  end: 'Data final planejada',
};

export interface GanttProTask {
  /** EDT do GanttPRO ("1.4.2.1.3"): a chave das predecessoras. */
  readonly wbsCode: string;
  /** Profundidade da EDT: a torre é 2, a disciplina 3, o serviço 4, a tarefa 5. */
  readonly level: number;
  /** Grupo de tarefas (tem filhos) — não é item de cronograma. */
  readonly isGroup: boolean;
  readonly name: string;
  /** Nomes dos grupos acima, do mais alto para o mais baixo: `['TORRE 2', 'FUNDAÇÃO', …]`. */
  readonly groupPath: readonly string[];
  /** `AAAA-MM-DD`. */
  readonly start: string;
  readonly end: string;
  /** "Duração (horas)" do arquivo, na jornada de 8 h. */
  readonly durationHours: number | null;
  /** "Horas estimadas": horas-homem. */
  readonly laborHours: number | null;
  readonly predecessorWbsCodes: readonly string[];
  readonly progressPct: number;
  /** Linha do Excel (a primeira é 1). */
  readonly rowNumber: number;
}

export interface GanttProReading {
  /** Tarefas **e grupos**, na ordem do arquivo. */
  readonly tasks: readonly GanttProTask[];
  /** Linhas que não puderam ser lidas — o arquivo continua valendo. */
  readonly issues: readonly RowIssue[];
  /** Linha do Excel em que o cabeçalho foi achado. */
  readonly headerRowNumber: number;
}

interface RawRow {
  readonly rowNumber: number;
  readonly wbsCode: string;
  readonly name: string;
  readonly cells: readonly unknown[];
}

/** A matriz tem o cabeçalho do GanttPRO? Serve para a tela decidir qual leitor usar. */
export function looksLikeGanttPro(matrix: Matrix): boolean {
  return findHeaderRow(matrix, [HEADERS.wbs, HEADERS.name], HEADER_SCAN_LIMIT) !== undefined;
}

export function readGanttProTasks(matrix: Matrix): Result<GanttProReading, DomainError> {
  const header = findHeaderRow(matrix, [HEADERS.wbs, HEADERS.name], HEADER_SCAN_LIMIT);
  if (header === undefined) {
    return err(
      domainError(
        GANTTPRO_ERROR.HEADER_NOT_FOUND,
        'Não encontrei o cabeçalho do GanttPRO — as colunas "Número de EDT" e "Nome da tarefa" ' +
          `nas primeiras ${String(HEADER_SCAN_LIMIT)} linhas. Este arquivo é uma exportação do ` +
          'GanttPRO em planilha?',
      ),
    );
  }

  const column = (spellings: readonly string[]) => columnOf(header.columns, spellings);
  const wbsColumn = column(HEADERS.wbs);
  const nameColumn = column(HEADERS.name);
  const startColumn = column(HEADERS.start);
  const endColumn = column(HEADERS.end);

  const missing: string[] = [];
  if (startColumn === undefined) missing.push(HEADER_LABEL.start);
  if (endColumn === undefined) missing.push(HEADER_LABEL.end);
  if (
    wbsColumn === undefined ||
    nameColumn === undefined ||
    startColumn === undefined ||
    endColumn === undefined
  ) {
    return err(
      domainError(
        GANTTPRO_ERROR.MISSING_COLUMNS,
        `Faltam colunas no arquivo do GanttPRO: ${missing.join(', ')}. ` +
          'Exporte o cronograma com as datas planejadas.',
        { missing },
      ),
    );
  }

  const progressColumn = column(HEADERS.progress);
  const durationColumn = column(HEADERS.duration);
  const laborColumn = column(HEADERS.labor);
  const predecessorsColumn = column(HEADERS.predecessors);
  const typeColumn = column(HEADERS.type);
  const levelColumn = column(HEADERS.level);

  // O nome fica entre a coluna do EDT (exclusive) e a "Nome da tarefa" (inclusive).
  const nameFrom = Math.min(wbsColumn, nameColumn) + 1;
  const nameTo = Math.max(wbsColumn, nameColumn);

  const issues: RowIssue[] = [];
  const raws: RawRow[] = [];

  for (let index = header.rowIndex + 1; index < matrix.length; index += 1) {
    const cells = matrix[index] ?? [];
    const wbsCode = cleanText(cells[wbsColumn]);
    // Rodapé do GanttPRO, linha em branco, banner repetido: sem EDT não é tarefa.
    if (wbsCode === null || !WBS_PATTERN.test(wbsCode)) continue;

    const rowNumber = index + 1;
    const name = firstText(cells, nameFrom, nameTo);
    if (name === null) {
      issues.push({ rowNumber, message: `A tarefa ${wbsCode} está sem nome.` });
      continue;
    }
    raws.push({ rowNumber, wbsCode, name, cells });
  }

  if (raws.length === 0) {
    return err(
      domainError(
        GANTTPRO_ERROR.NO_TASKS,
        'O arquivo tem o cabeçalho do GanttPRO, mas nenhuma tarefa abaixo dele.',
      ),
    );
  }

  const nameByWbs = new Map<string, string>();
  for (const raw of raws) {
    if (nameByWbs.has(raw.wbsCode)) {
      issues.push({
        rowNumber: raw.rowNumber,
        message: `A EDT ${raw.wbsCode} aparece mais de uma vez no arquivo.`,
      });
      continue;
    }
    nameByWbs.set(raw.wbsCode, raw.name);
  }
  const parentsWithChildren = parentsOf(raws.map((raw) => raw.wbsCode));

  const tasks: GanttProTask[] = [];

  for (const raw of raws) {
    const { rowNumber, wbsCode, name, cells } = raw;

    const start = readDate(cells[startColumn]);
    const end = readDate(cells[endColumn]);
    if (!start.ok || start.value === null || !end.ok || end.value === null) {
      issues.push({
        rowNumber,
        message: `"${name}" (EDT ${wbsCode}) está sem data de início ou de fim legível.`,
      });
      continue;
    }

    const labor = optionalNumber(cells, laborColumn);
    if (!labor.ok) {
      issues.push({
        rowNumber,
        message: `"${name}" (EDT ${wbsCode}): "Horas estimadas" não é um número.`,
      });
    }

    const typeText = typeColumn === undefined ? null : cleanText(cells[typeColumn]);
    const isGroup =
      typeText === null
        ? parentsWithChildren.has(wbsCode)
        : typeText.toLowerCase().includes('grupo');

    const declaredLevel = levelColumn === undefined ? null : levelOf(cells[levelColumn]);
    const progress = optionalNumber(cells, progressColumn);

    tasks.push({
      wbsCode,
      level: declaredLevel ?? wbsCode.split('.').length,
      isGroup,
      name,
      groupPath: ancestorNames(wbsCode, nameByWbs),
      start: start.value,
      end: end.value,
      durationHours: valueOf(optionalNumber(cells, durationColumn)),
      laborHours: labor.ok ? labor.value : null,
      predecessorWbsCodes:
        predecessorsColumn === undefined ? [] : readWbsList(cells[predecessorsColumn]),
      progressPct: progress.ok ? (progress.value ?? 0) : 0,
      rowNumber,
    });
  }

  return ok({ tasks, issues, headerRowNumber: header.rowIndex + 1 });
}

/** A primeira célula de texto no trecho `[from, to]`. */
function firstText(cells: readonly unknown[], from: number, to: number): string | null {
  for (let index = from; index <= to; index += 1) {
    const text = cleanText(cells[index]);
    if (text !== null) return text;
  }
  return null;
}

/** Lê a coluna quando ela existe; coluna ausente é célula vazia, e não erro. */
function optionalNumber(cells: readonly unknown[], column: number | undefined): Cell<number> {
  return column === undefined ? { ok: true, value: null } : readNumber(cells[column]);
}

function valueOf(cell: Cell<number>): number | null {
  return cell.ok ? cell.value : null;
}

function levelOf(value: unknown): number | null {
  const parsed = readNumber(value);
  if (!parsed.ok || parsed.value === null) return null;
  return Number.isInteger(parsed.value) && parsed.value > 0 ? parsed.value : null;
}

/** Toda EDT que é prefixo de outra: quem tem filhos. */
function parentsOf(codes: readonly string[]): Set<string> {
  const parents = new Set<string>();
  for (const code of codes) {
    const pieces = code.split('.');
    for (let depth = 1; depth < pieces.length; depth += 1) {
      parents.add(pieces.slice(0, depth).join('.'));
    }
  }
  return parents;
}

/** Nomes dos ancestrais de uma EDT que existem no arquivo, do topo para baixo. */
function ancestorNames(wbsCode: string, nameByWbs: ReadonlyMap<string, string>): string[] {
  const pieces = wbsCode.split('.');
  const names: string[] = [];
  for (let depth = 1; depth < pieces.length; depth += 1) {
    const name = nameByWbs.get(pieces.slice(0, depth).join('.'));
    if (name !== undefined) names.push(name);
  }
  return names;
}
